import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'
import Column from '@amzn/meridian/column'
import Heading from '@amzn/meridian/heading'
import Text from '@amzn/meridian/text'
import React, { ReactElement, Children, useEffect, useRef, useState } from 'react'
import { css, cx } from 'emotion'

import { makeCrossFrameIhmIdentityBrokerEncryptedTokenRetrieverRequest as iibEncryptedTokenRetriever } from './CrossFrameIhmIdentityBrokerEncryptedTokenRetriever'
import './Announcements.scss'
import { Content } from '@amzn/coral_com-amazon-ihmlearninganddevelopmentservice'
import { ExpandableImage } from '@amzn/ihm-associate-tools-meridian-components'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle'
import Row from '@amzn/meridian/row'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'

export interface IAnnouncementsModalProps {
    /**
     * The content to display in the modal.
     */
    announcementContent: Content,

    /**
     * Callback function that renders the modal navigation buttons.
     */
    renderButton: (canAdvance: boolean) => React.ReactNode,

    /**
     * Callback function to render the progress tracker component.
     */
    renderProgressTracker: () => React.ReactNode,

    /**
     * Callback function to skip the announcement content and hide the modal.
     */
    clearAnnouncementContent: () => void
}

/**
 * @interface ICloseButtonProps
 * @param onClick - Function that is called when the close button is clicked.
 */
interface ICloseButtonProps {
    onClick: () => void
}

/**
 * The CloseButton component allows the user to close a modal.
 * @param props - CloseButtonProps containing a callback method to call when the close button is clicked.
 */
export const CloseButton = (props: ICloseButtonProps): ReactElement => {
    const [bundle] = useBundle(BUNDLE_NAME)

    return (
        <div className="announcements-close-button-div">
            <Button className="announcements-close-button" data-testid="announcements-close-button" type="link" size="xlarge" onClick={props.onClick}>
                <Icon tokens={closeCircleTokens}>{t(bundle, 'jsma-web-common-close')}</Icon>
            </Button>
        </div>
    )
}

const _renderModalText = (textToDisplay?: string) => {
    if (!textToDisplay) {
        return null
    }

    const splitByNewLine: string[] = textToDisplay.split(/\n/)
    // Meridian <Text> does not format \n separators. Add break line <br/> for each line instead.
    // Children.toArray adds keys automatically: https://react.dev/reference/react/Children#children-toarray
    return (
        <Text data-testid="announcements-modal-body-text" className={cx(css({ lineHeight: '20px' }))}>
            {Children.toArray(splitByNewLine.map((text: string) => <>{text}<br/></>))}
        </Text>
    )
}

// Stores the creative IDs of video creatives that the associate has already completed.
const completedVideoCreativeIdSet = new Set()

export const AnnouncementsModal = ({
    announcementContent,
    renderButton,
    renderProgressTracker,
    clearAnnouncementContent,
}: IAnnouncementsModalProps): ReactElement => {
    const { url, graphicType, header, text } = announcementContent.creative

    const [bundle] = useBundle(BUNDLE_NAME)
    const videoRef = useRef<HTMLVideoElement>(null)
    const [canAdvance, setCanAdvance] = useState(graphicType !== 'VIDEO')
    const [graphic, setGraphic] = useState<ReactElement | null>(null)
    const [isSurveyOrQuiz, setIsSurveyOrQuiz] = useState(graphicType === 'SURVEY' || graphicType === 'QUIZ')

    const formulateSuffix = 'formulate.gsf.a2z.com'
    // Titles the embedded Formulate iframe, so they are read out by screen readers
    // and must be translated.
    const quizTitle = t(bundle, 'jsma-web-announcements-quizTitle')
    const surveyTitle = t(bundle, 'jsma-web-announcements-surveyTitle')
    const quizFrameId = 'quiz-frame'
    const surveyFrameId = 'survey-frame'

    const setVideoFullScreen = (): void => {
        videoRef.current?.requestFullscreen()
    }

    const onVideoEnded = (): void => {
        // Would be nice, but Typescript doesn't allow this, and browser support is spotty
        // videoRef.current!.exitFullScreen();
        setCanAdvance(true)
        completedVideoCreativeIdSet.add(announcementContent.creative.creativeId)
    }

    useEffect(() => {
        setGraphic(null)
        setCanAdvance(graphicType !== 'VIDEO' || completedVideoCreativeIdSet.has(announcementContent.creative.creativeId))
        setIsSurveyOrQuiz(graphicType === 'SURVEY' || graphicType === 'QUIZ')
    }, [announcementContent])

    useEffect(() => {
        if (graphicType === 'VIDEO') {
            videoRef.current?.load()
        }
        applyGraphicByType()
    }, [graphicType, url])

    /**
     * Returns quiz/survey link with the iib encryptedAuthToken and iibRegion in the url params appended.
     * 1. Fetch Encrypted Auth Token by making a cross-frame call.
     * 2. Fetch region
     * 3. Set encryptedAuthToken, iibRegion and iibRequest in the url parameters before returning it.
     */
    const setUrlParamsWithIIBToken = async (): Promise<string> => {
        let sourceUrl: string = url! // url is required as defined in the model so this can't be undefined
        await iibEncryptedTokenRetriever()
            .then((getEncryptedTokenResponse) => {
                const encryptedAuthToken = getEncryptedTokenResponse?.encryptedToken
                if (!encryptedAuthToken) {
                    throw new Error(`AnnouncementModal: No encryptedAuthToken retrieved to fetch ${graphicType}.`)
                }
                const region = EnvironmentDetail.getRegion()
                if (!region) {
                    throw new Error(`AnnouncementModal: Couldn't find region from appInfo to fetch ${graphicType}.`)
                }
                console.log(`AnnouncementModal: Region is `, region)

                sourceUrl = `${url}?encryptedAuthToken=${encodeURIComponent(
                    encryptedAuthToken,
                )}&iibRequest=true&iibRegion=${region}`
            })
            .catch((error) => {
                console.error('AnnouncementModal: An error occurred while generating Formulate URL (with iib).', error)
            })
        return sourceUrl
    }

    const isMediaHostedOnFormulate = () => {
        return url?.includes(formulateSuffix)
    }

    const applyGraphicByType = async (): Promise<void> => {
        if (graphicType && url) {
            switch (graphicType) {
            case 'IMAGE':
                setGraphic(<ExpandableImage className="announcements-modal-image" src={url}/>)
                break
            case 'VIDEO':
                setGraphic(
                        <div className="announcements-video-div">
                            <video ref={videoRef} className="announcements-modal-image" data-testid="announcements-modal-video" onPlay={setVideoFullScreen} onEnded={onVideoEnded} controls={true}>
                                <source data-testid="announcements-modal-video-source" src={url} type="video/mp4"/>
                            </video>
                        </div>,
                    )
                break
            case 'SURVEY':
                // explicit fall-through
            case 'QUIZ':
                const title = graphicType === 'QUIZ' ? quizTitle : surveyTitle
                const id = graphicType === 'QUIZ' ? quizFrameId : surveyFrameId

                let urlWithIIBToken
                if (isMediaHostedOnFormulate()) {
                    if (EnvironmentDetail.isIIBAuthN()) {
                        urlWithIIBToken = await setUrlParamsWithIIBToken()
                        console.log(`Loading Formulate using URL with IIB token`)
                    }
                }

                setGraphic(<iframe data-testid="announcements-modal-iframe" src={urlWithIIBToken ?? url} width="100%" height="100%" title={title} id={id}/>)
                break
            }
        }
    }

    const renderQuizOrSurvey = (): ReactElement | null => {
        return (
            <div className="announcements-modal-graphic-survey">
                <div className={!(isMediaHostedOnFormulate() && !canAdvance) ? 'announcements-not-visible' : ''} />
                {graphic}
            </div>
        )
    }

    return (
        <>
            <div data-testid="announcements-overlay-wrapper" className="announcements-overlay-wrapper"/>
            <div data-testid="announcements-overlay-container" className="announcements-overlay-container">
                <CloseButton onClick={clearAnnouncementContent} />
                {isSurveyOrQuiz ? renderQuizOrSurvey() : <div data-testid="announcements-modal-graphic" className="announcements-modal-graphic">{graphic}</div>}
                <div className={isSurveyOrQuiz ? 'announcements-middle-padding-quiz' : 'announcements-middle-padding'} />
                <Column spacing="medium">
                    <Heading data-testid="announcements-content-heading" level={6} alignment="center">
                        {header}
                    </Heading>
                    {_renderModalText(text)}
                    <Column alignmentHorizontal="center">
                        {renderProgressTracker()}
                        {renderButton(canAdvance)}
                    </Column>
                </Column>
            </div>
        </>
    )
}
